import React, { useState } from 'react';
import { Card, PageTemplate, Button, Input, Alert } from '../components/ui';
import { 
  FiMapPin, 
  FiPhone, 
  FiMail, 
  FiClock,
  FiExternalLink,
  FiSend
} from 'react-icons/fi';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message: '' }

  const contactInfo = [
    {
      icon: FiMapPin,
      title: 'Address',
      content: 'Arts Tech Lab, Run Run Shaw Tower (RRST-4.35)',
      subtitle: 'Centennial Campus, The University of Hong Kong, Pok Fu Lam, Hong Kong',
      color: 'primary'
    },
    {
      icon: FiPhone,
      title: 'Phone',
      content: '(+852) 3917 5801',
      color: 'success'
    },
    {
      icon: FiMail,
      title: 'Email',
      content: 'atlab@hku.hk',
      href: 'mailto:atlab@hku.hk',
      color: 'info'
    },
    {
      icon: FiClock,
      title: 'Office Hours',
      content: '09:30 – 17:30',
      subtitle: 'Monday to Friday',
      color: 'warning'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Message sent successfully! We will get back to you soon.' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus({ type: 'error', message: data.message || 'Failed to send message.' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus({ type: 'error', message: 'An error occurred. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <PageTemplate
      title="Contact Us"
      description="Get in touch with the Arts Technology Lab team"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Contact Information */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-200">
                <Card.Content className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className={`p-3 rounded-lg bg-${info.color}-100 dark:bg-${info.color}-900/20 flex-shrink-0`}>
                      <info.icon className={`w-6 h-6 text-${info.color}-600 dark:text-${info.color}-400`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif font-medium text-lg mb-2 text-gray-900 dark:text-white">
                        {info.title}
                      </h3>
                      {info.href ? (
                        <a 
                          href={info.href}
                          className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-literary transition-colors duration-200 flex items-center"
                        >
                          {info.content}
                          <FiExternalLink className="w-4 h-4 ml-2" />
                        </a>
                      ) : (
                        <p className="text-gray-800 dark:text-gray-200 font-literary">
                          {info.content}
                        </p>
                      )}
                      {info.subtitle && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 font-literary mt-1">
                          {info.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </Card.Content>
              </Card>
            ))}
          </div>

          <Card>
            <Card.Header>
              <Card.Title>Visit Us</Card.Title>
              <Card.Description>Find us on the Centennial Campus</Card.Description>
            </Card.Header>
            <Card.Content>
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <p className="text-sm font-literary text-primary-700 dark:text-gray-300 leading-relaxed">
                  The Arts Technology Lab is located in Room 4.35 of the Run Run Shaw Tower on the Centennial Campus. 
                  We're open during regular business hours and welcome visitors who want to learn more about our 
                  facilities and ongoing projects.
                </p>
              </div>
            </Card.Content>
          </Card>
        </div>

        {/* Contact Form */}
        <div>
          <Card className="h-full">
            <Card.Header>
              <Card.Title>Send us a Message</Card.Title>
              <Card.Description>Have a question? Fill out the form below and we'll get back to you.</Card.Description>
            </Card.Header>
            <Card.Content>
              {status && (
                <div className="mb-6">
                  <Alert variant={status.type} onClose={() => setStatus(null)}>
                    {status.message}
                  </Alert>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                />
                
                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                />
                
                <Input
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  required
                />
                
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    placeholder="Type your message here..."
                    required
                  ></textarea>
                </div>
                
                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-full justify-center"
                    loading={loading}
                    icon={FiSend}
                  >
                    Send Message
                  </Button>
                </div>
              </form>
            </Card.Content>
          </Card>
        </div>
      </div>
    </PageTemplate>
  );
};

export default Contact;
